import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Plus, Edit2, Trash2, X, Building2, Phone, User, Search } from 'lucide-react';

const defaultForm = {
  name: '', noOfFlats: 0, area: '', address: '', totalFloors: 0,
  inchargeName: '', inchargePosition: '', inchargeMobile: '', remarks: '', isActive: true
};

export default function ApartmentsPage() {
  const [apts, setApts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editApt, setEditApt] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(defaultForm);

  const fetchApts = useCallback(async () => {
    try { const res = await api.getApartments(); setApts(res.data); }
    catch (err) { console.error('Failed to load apartments:', err.message); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchApts(); }, [fetchApts]);

  const openNew = () => { setForm({ ...defaultForm }); setEditApt(null); setShowModal(true); };
  const openEdit = (a) => {
    setForm({
      name: a.name || '', noOfFlats: a.noOfFlats || 0, area: a.area || '', address: a.address || '',
      totalFloors: a.totalFloors || 0, inchargeName: a.inchargeName || '', inchargePosition: a.inchargePosition || '',
      inchargeMobile: a.inchargeMobile || '', remarks: a.remarks || '', isActive: a.isActive !== false
    });
    setEditApt(a);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editApt) { await api.updateApartment(editApt.id, form); }
      else { await api.createApartment(form); }
      setShowModal(false);
      fetchApts();
    } catch (err) { console.error('Save failed:', err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this apartment?')) return;
    await api.deleteApartment(id);
    fetchApts();
  };

  const filtered = search
    ? apts.filter(a => a.name.toLowerCase().includes(search.toLowerCase()) || (a.area || '').toLowerCase().includes(search.toLowerCase()))
    : apts;

  const inputStyle = { background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' };

  return (
    <div data-testid="apartments-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Apartments / Buildings</h2>
          <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>{apts.length} apartments registered</p>
        </div>
        <button data-testid="add-apartment-button" onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold" style={{ background: '#C8A96E', color: '#000' }}>
          <Plus className="w-4 h-4" /> Add Apartment
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#78716C' }} />
        <input data-testid="apt-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search apartments..." className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }} />
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: '#78716C' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', color: '#78716C' }}>
          <Building2 className="w-12 h-12 mx-auto mb-3" style={{ color: '#2A241F' }} />
          <p>{search ? 'No matching apartments' : 'No apartments added yet'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((a, i) => (
            <div key={a.id} data-testid={`apt-card-${a.id}`} className="rounded-xl p-5 transition-all duration-200 hover:-translate-y-1 fade-in"
              style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', animationDelay: `${i * 50}ms`, opacity: a.isActive === false ? 0.5 : 1 }}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,169,110,0.1)' }}>
                    <Building2 className="w-5 h-5" style={{ color: '#C8A96E' }} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-base" style={{ color: '#F5F5F4' }}>{a.name}</h4>
                    {a.area && <p className="text-xs mt-0.5" style={{ color: '#A8A29E' }}>{a.area}</p>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-apt-${a.id}`} onClick={() => openEdit(a)} className="p-1.5 rounded-lg transition-colors" style={{ color: '#A8A29E' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#C8A96E'} onMouseLeave={e => e.currentTarget.style.color = '#A8A29E'}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`delete-apt-${a.id}`} onClick={() => handleDelete(a.id)} className="p-1.5 rounded-lg transition-colors" style={{ color: '#A8A29E' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#F87171'} onMouseLeave={e => e.currentTarget.style.color = '#A8A29E'}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                {a.noOfFlats > 0 && (
                  <div className="rounded-lg p-2 text-center" style={{ background: '#100E0C', border: '1px solid #2A241F' }}>
                    <div className="text-lg font-bold" style={{ fontFamily: 'Outfit', color: '#C8A96E' }}>{a.noOfFlats}</div>
                    <div className="text-xs" style={{ color: '#78716C' }}>Flats</div>
                  </div>
                )}
                {a.totalFloors > 0 && (
                  <div className="rounded-lg p-2 text-center" style={{ background: '#100E0C', border: '1px solid #2A241F' }}>
                    <div className="text-lg font-bold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{a.totalFloors}</div>
                    <div className="text-xs" style={{ color: '#78716C' }}>Floors</div>
                  </div>
                )}
              </div>

              {a.address && <p className="text-xs mb-2" style={{ color: '#78716C' }}>{a.address}</p>}

              {/* In-charge details */}
              {a.inchargeName && (
                <div className="rounded-lg p-3 mt-2" style={{ background: '#100E0C', border: '1px solid #2A241F' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <User className="w-3.5 h-3.5" style={{ color: '#C8A96E' }} />
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#C8A96E' }}>In-charge</span>
                  </div>
                  <div className="text-sm font-medium" style={{ color: '#F5F5F4' }}>{a.inchargeName}</div>
                  {a.inchargePosition && <div className="text-xs" style={{ color: '#A8A29E' }}>{a.inchargePosition}</div>}
                  {a.inchargeMobile && (
                    <a href={`tel:${a.inchargeMobile}`} className="inline-flex items-center gap-1 text-xs mt-1" style={{ color: '#C8A96E' }}>
                      <Phone className="w-3 h-3" />{a.inchargeMobile}
                    </a>
                  )}
                </div>
              )}

              {a.remarks && <p className="text-xs mt-2 italic" style={{ color: '#78716C' }}>{a.remarks}</p>}

              {a.isActive === false && (
                <div className="mt-2"><span className="badge-inactive px-2 py-0.5 rounded-full text-xs font-semibold">Inactive</span></div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-2xl rounded-xl max-h-[90vh] overflow-y-auto" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.3)' }}>
            <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid #2A241F' }}>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{editApt ? 'Edit Apartment' : 'Add Apartment'}</h3>
              <button data-testid="close-apt-modal" onClick={() => setShowModal(false)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-5">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Apartment / Building Name *</label>
                  <input data-testid="apt-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Aspire Heights" className="w-full px-3 py-2.5 rounded-lg text-sm" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>No. of Flats</label>
                  <input data-testid="apt-flats" type="number" min={0} value={form.noOfFlats} onChange={e => setForm({...form, noOfFlats: parseInt(e.target.value) || 0})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Total Floors</label>
                  <input data-testid="apt-floors" type="number" min={0} value={form.totalFloors} onChange={e => setForm({...form, totalFloors: parseInt(e.target.value) || 0})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Area Name</label>
                  <input data-testid="apt-area" value={form.area} onChange={e => setForm({...form, area: e.target.value})} placeholder="e.g. Whitefield, Marathahalli" className="w-full px-3 py-2.5 rounded-lg text-sm" style={inputStyle} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Exact Address</label>
                  <input data-testid="apt-address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Full address with landmark" className="w-full px-3 py-2.5 rounded-lg text-sm" style={inputStyle} />
                </div>
              </div>

              {/* In-charge Section */}
              <div className="rounded-lg p-4" style={{ background: '#100E0C', border: '1px solid rgba(200,169,110,0.2)' }}>
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit', color: '#C8A96E' }}>
                  <User className="w-4 h-4" /> Apartment In-charge / POC
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#A8A29E' }}>In-charge Name</label>
                    <input data-testid="apt-incharge-name" value={form.inchargeName} onChange={e => setForm({...form, inchargeName: e.target.value})} placeholder="Contact person name" className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#161311', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#A8A29E' }}>Position / Role</label>
                    <input data-testid="apt-incharge-position" value={form.inchargePosition} onChange={e => setForm({...form, inchargePosition: e.target.value})} placeholder="e.g. Secretary, President, Manager" className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#161311', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#A8A29E' }}>POC Mobile No.</label>
                    <input data-testid="apt-incharge-mobile" value={form.inchargeMobile} onChange={e => setForm({...form, inchargeMobile: e.target.value})} placeholder="10-digit mobile" maxLength={10} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#161311', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Remarks</label>
                <textarea data-testid="apt-remarks" value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} rows={2} placeholder="Any notes about this apartment..." className="w-full px-3 py-2.5 rounded-lg text-sm resize-none" style={inputStyle} />
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#C8A96E' }}>Active</label>
                <button type="button" onClick={() => setForm({...form, isActive: !form.isActive})}
                  className="w-10 h-5 rounded-full transition-all relative"
                  style={{ background: form.isActive ? '#C8A96E' : '#2A241F' }}>
                  <div className="w-4 h-4 rounded-full absolute top-0.5 transition-all" style={{ background: '#F5F5F4', left: form.isActive ? '22px' : '2px' }} />
                </button>
              </div>

              <div className="flex gap-3 pt-2">
                <button data-testid="save-apt-button" type="submit" className="px-6 py-2.5 rounded-lg text-sm font-semibold" style={{ background: '#C8A96E', color: '#000' }}>
                  {editApt ? 'Update Apartment' : 'Add Apartment'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2.5 rounded-lg text-sm font-medium" style={{ background: '#2A241F', color: '#A8A29E' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
